import { useEffect, useState } from 'react';
import api from '../api/axios';
import PageTitle from '../components/PageTitle';

const STATUS_STYLE = {
  PENDING:   { label: 'Pending',   icon: '⏳', cls: 'badge-under_review' },
  INITIATED: { label: 'Initiated', icon: '🔄', cls: 'badge-submitted'    },
  PAID:      { label: 'Paid',      icon: '✅', cls: 'badge-acknowledged'  },
  FAILED:    { label: 'Failed',    icon: '❌', cls: 'badge-rejected'      },
  REFUNDED:  { label: 'Refunded', icon: '↩️', cls: 'badge-under_review'  },
};

function PayBadge({ status }) {
  const s = STATUS_STYLE[status] || { label: status, icon: '•', cls: '' };
  return <span className={`badge ${s.cls}`}>{s.icon} {s.label}</span>;
}

export default function AdminPayments() {
  const [payments, setPayments] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [filter, setFilter]     = useState('ALL');
  const [search, setSearch]     = useState('');
  const [stats, setStats]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [refundId, setRefundId] = useState(null);
  const [refundNote, setRefundNote] = useState('');
  const [msg, setMsg]           = useState(null);

  useEffect(() => {
    Promise.all([api.get('/api/payment/all'), api.get('/api/payment/stats')])
      .then(([p, s]) => { setPayments(p.data); setFiltered(p.data); setStats(s.data); })
      .catch(console.error).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let list = payments;
    if (filter !== 'ALL') list = list.filter(p => p.status === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(p =>
        p.userName?.toLowerCase().includes(q) ||
        p.userEmail?.toLowerCase().includes(q) ||
        p.serviceDesc?.toLowerCase().includes(q) ||
        p.invoiceNumber?.toLowerCase().includes(q)
      );
    }
    setFiltered(list);
  }, [filter, search, payments]);

  const handleRefund = async (id) => {
    try {
      const { data } = await api.patch(`/api/payment/${id}/refund?note=${encodeURIComponent(refundNote)}`);
      setPayments(prev => prev.map(p => p.id === id ? data : p));
      setRefundId(null); setRefundNote('');
      setMsg({ type: 'success', text: 'Marked as refunded' });
      setTimeout(() => setMsg(null), 3000);
    } catch (err) {
      setMsg({ type: 'error', text: 'Refund failed' });
    }
  };

  if (loading) return <div className="centered"><span className="spinner spinner-dark" /></div>;

  return (
    <>
      <PageTitle title="Payments" />
      <div className="page-hero">
        <div className="page-hero-label">Admin Panel</div>
        <h1>Payments & Revenue</h1>
        <p>{payments.length} transactions</p>
      </div>
      <div className="page-wide">

        {/* Stats */}
        <div className="stat-grid" style={{ marginBottom: '1.5rem' }}>
          {[
            { label: 'Total Revenue', value: `₹${Number(stats?.revenue || 0).toLocaleString('en-IN')}`, icon: '💰', big: true },
            { label: 'Paid',          value: stats?.paid    || 0, icon: '✅' },
            { label: 'Pending',       value: stats?.pending || 0, icon: '⏳', highlight: true },
            { label: 'Failed',        value: stats?.failed  || 0, icon: '❌' },
          ].map(s => (
            <div key={s.label} className="stat-card"
              style={s.highlight && s.value > 0 ? { border: '2px solid var(--gold)' } : {}}>
              <div style={{ fontSize: '1.3rem', marginBottom: '.2rem' }}>{s.icon}</div>
              <div className={`stat-num ${s.big ? '' : ''}`}
                style={s.big ? { fontSize: '1.5rem', color: 'var(--green)' } : {}}>
                {s.value}
              </div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {msg && <div className={`alert alert-${msg.type}`} style={{ marginBottom: '1.25rem' }}>{msg.text}</div>}

        {/* Filters */}
        <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
          <input placeholder="Search name, email, invoice, service..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: 220, padding: '.6rem 1rem', border: '1.5px solid var(--border)', borderRadius: 10, fontFamily: 'DM Sans, sans-serif', fontSize: '.88rem', background: '#fff', outline: 'none' }} />
          <select value={filter} onChange={e => setFilter(e.target.value)}
            style={{ padding: '.6rem 1rem', border: '1.5px solid var(--border)', borderRadius: 10, fontFamily: 'DM Sans, sans-serif', fontSize: '.88rem', background: '#fff' }}>
            <option value="ALL">All Statuses</option>
            {Object.keys(STATUS_STYLE).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <span style={{ fontSize: '.82rem', color: 'var(--muted)' }}>{filtered.length} results</span>
        </div>

        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead>
                <tr><th>Invoice</th><th>User</th><th>Service</th><th>Fee</th><th>GST</th><th>Total</th><th>Status</th><th>Date</th><th>Action</th></tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--muted)', padding: '2rem' }}>No payments found</td></tr>
                )}
                {filtered.map(p => (
                  <>
                    <tr key={p.id}>
                      <td style={{ fontFamily: 'monospace', fontSize: '.78rem', color: 'var(--blue)' }}>{p.invoiceNumber}</td>
                      <td>
                        <div style={{ fontSize: '.88rem', fontWeight: 600 }}>{p.userName}</div>
                        <div style={{ fontSize: '.75rem', color: 'var(--muted)' }}>{p.userEmail}</div>
                      </td>
                      <td style={{ fontSize: '.83rem', maxWidth: 180 }}>{p.serviceDesc}</td>
                      <td style={{ fontSize: '.85rem' }}>₹{Number(p.amount || 0).toLocaleString('en-IN')}</td>
                      <td style={{ fontSize: '.85rem', color: 'var(--muted)' }}>₹{Number(p.gstAmount || 0).toLocaleString('en-IN')}</td>
                      <td style={{ fontSize: '.88rem', fontWeight: 700, color: 'var(--navy)' }}>₹{Number(p.totalAmount || 0).toLocaleString('en-IN')}</td>
                      <td><PayBadge status={p.status} /></td>
                      <td style={{ fontSize: '.78rem', color: 'var(--muted)', whiteSpace: 'nowrap' }}>
                        {new Date(p.createdAt).toLocaleDateString('en-IN')}
                      </td>
                      <td>
                        {p.status === 'PAID' && (
                          <button className="btn btn-outline btn-sm"
                            style={{ color: 'var(--red)', borderColor: 'var(--red)', fontSize: '.72rem' }}
                            onClick={() => setRefundId(p.id)}>
                            Refund
                          </button>
                        )}
                      </td>
                    </tr>
                    {/* Inline refund form */}
                    {refundId === p.id && (
                      <tr key={`refund-${p.id}`}>
                        <td colSpan={9} style={{ background: '#FDEDEC', padding: '1rem' }}>
                          <div style={{ display: 'flex', gap: '.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <input placeholder="Reason for refund (optional)"
                              value={refundNote} onChange={e => setRefundNote(e.target.value)}
                              style={{ flex: 1, minWidth: 200, padding: '.45rem .8rem', border: '1px solid #f5b8b8', borderRadius: 8, fontSize: '.85rem' }} />
                            <button className="btn btn-danger btn-sm" onClick={() => handleRefund(p.id)}>Confirm Refund</button>
                            <button className="btn btn-outline btn-sm" onClick={() => setRefundId(null)}>Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
